import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { AssessmentData } from "./types";
import { apiUrl } from "@/config/api";

export const useAssessmentStore = defineStore("assessment", () => {
  // State
  const assessmentData = ref<AssessmentData | null>(null);
  const assessmentDataCache = ref<Record<string, AssessmentData>>({});
  const currentAssessmentId = ref<string | null>(null);

  // Getters
  const getCurrentAssessmentData = computed(() => {
    return (assessmentId: string): AssessmentData | null => {
      return assessmentDataCache.value[assessmentId] || null;
    };
  });

  // Actions
  const setAssessmentData = (assessmentId: string, data: AssessmentData) => {
    assessmentData.value = data;
    assessmentDataCache.value[assessmentId] = data;
    currentAssessmentId.value = assessmentId;
  };

  const setCurrentAssessment = (assessmentId: string) => {
    currentAssessmentId.value = assessmentId;
    assessmentData.value = assessmentDataCache.value[assessmentId] || null;
  };

  const fetchAssessmentData = async (assessmentId: string) => {
    try {
      // Check if we already have this assessment data cached
      if (assessmentDataCache.value[assessmentId]) {
        console.log(`Using cached assessment data for ID: ${assessmentId}`);
        setCurrentAssessment(assessmentId);
        return;
      }

      console.log(`Fetching assessment data for ID: ${assessmentId}`);
      const response = await fetch(
        apiUrl(`/assessment_responses/${assessmentId}`),
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = (await response.json()) as AssessmentData;
      setAssessmentData(assessmentId, data);
    } catch (error) {
      console.error("Failed to fetch assessment data:", error);
      // Re-throw the error to allow the component to catch it
      throw error;
    }
  };

  return {
    // State - expose ref values directly for proper DevTools display
    assessmentData,
    assessmentDataCache,
    currentAssessmentId,

    // Getters
    getCurrentAssessmentData,

    // Actions
    setAssessmentData,
    setCurrentAssessment,
    fetchAssessmentData,
  };
});
